import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { ReactSVG } from "react-svg";
import SearchArrow from '../../../../../../assets/images/select-down-arrow.svg';
import SearchUpArrow from '../../../../../../assets/images/select-up-arrow-black.svg';
import css from "../style.scss";
import { v4 as uuidv4 } from 'uuid';

const getDecimalPlaces = (value) => {
  if (!Number.isFinite(value)) return 0; // Handle non-numeric cases
  let str = value.toString();
  if (str.includes('.')) {
      return str.split('.')[1].length;
  }
  return 0;
}

const roundToDynamic = (value) => {
  let decimals = getDecimalPlaces(value);
  let factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

// Rounds a decimal STRING to `decimals` places using standard (round-half-up) rounding,
// operating on the digits directly so binary float error can't turn e.g. "6.255" into 6.25.
const roundHalfUpToDecimals = (value, decimals) => {
  const str = String(value);
  const negative = str.startsWith('-');
  const unsigned = negative ? str.slice(1) : str;
  const [intPart = '0', fracPart = ''] = unsigned.split('.');

  if (fracPart.length <= decimals) {
    return Number(str);
  }

  const keptFrac = fracPart.slice(0, decimals);
  const roundUp = Number(fracPart.charAt(decimals)) >= 5;
  const digits = `${intPart}${keptFrac}`.split('').map(Number);

  if (roundUp) {
    let i = digits.length - 1;
    while (i >= 0) {
      digits[i] += 1;
      if (digits[i] === 10) {
        digits[i] = 0;
        i -= 1;
      } else {
        break;
      }
    }
    if (i < 0) digits.unshift(1);
  }

  const digitsStr = digits.join('');
  const newIntPart = digitsStr.slice(0, digitsStr.length - decimals) || '0';
  const newFracPart = digitsStr.slice(digitsStr.length - decimals);
  const rounded = decimals > 0 ? `${newIntPart}.${newFracPart}` : newIntPart;
  return Number(`${negative ? '-' : ''}${rounded}`);
};

const AutoSearchDropdown = ({ items, placeHolder, onChangeValue , defaultValue , searchDisabled = false, allowNegativeValue = false , isPercent = false, maxLength = null, decimalPlaces = 0 }) => {

  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [options, setOptions] = useState(items || []);
  const [activeIndex, setActiveIndex] = useState(-1); // Track the active dropdown option
  const InputContainerRef = useRef(null);
  const inputRef = useRef(null);
  const optionsRefs = useRef([]); // Store refs for each dropdown option
  const optionSelectedRef = useRef(false);

  useEffect(() => {
    const formatDefaultValue = () => {
      if (defaultValue === undefined || defaultValue === null || defaultValue === '') {
        return '';
      }
      if (!isPercent || defaultValue === '0') {
        return String(defaultValue);
      }
      const scaled = Number(defaultValue) * 100;
      return String(decimalPlaces > 0 ? roundHalfUpToDecimals(scaled, decimalPlaces) : roundToDynamic(scaled));
    };

    setSearchTerm(formatDefaultValue());
  }, [defaultValue, isPercent, decimalPlaces]);



  const handleInputChange = (e) => {
    if (!searchDisabled) {
      let value;
      if (decimalPlaces > 0) {
        value = allowNegativeValue
          ? e.target.value.replace(/(?!^-)-|[^\d.-]/g, '')
          : e.target.value.replace(/[^\d.]/g, '');
        // Keep only the first decimal point typed
        const firstDot = value.indexOf('.');
        if (firstDot !== -1) {
          value = value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, '');
        }
      } else {
        value = allowNegativeValue
          ? e.target.value.replace(/(?!^-)-|[^\d-]/g, '')
          : e.target.value.replace(/\D/g, '');
      }
      if (value === '-0') value = '0';
      setSearchTerm(value);

      const numericValue = isPercent && value ? Number(value) / 100 : value;
      const searchValue = String(numericValue).toLowerCase();
      const filteredSuggestions = items?.filter(item =>
        String(item?.name)?.toLowerCase()?.includes(searchValue)
      );

      // setOptions(filteredSuggestions.length ? filteredSuggestions : (items || []));
      setActiveIndex(-1); // Reset active index when input changes
      setIsFocused(true);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleOutsideClick = (e) => {
    if (InputContainerRef.current && !InputContainerRef.current.contains(e.target)) {
      setIsFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const optionSelected = (option) => {
    optionSelectedRef.current = true;
    const displayValue = isPercent ? String(option.value) : String(option.value);
    setSearchTerm(displayValue);
    setIsFocused(false);
    inputRef.current?.blur();

   const normalizedValue = isPercent && !isNaN(option.value)
      ? Number(option.value) / 100
      : option.value;

    onChangeValue?.(normalizedValue);

    // Keep flag true slightly longer to prevent blur overwrite
    setTimeout(() => {
      optionSelectedRef.current = false;
    }, 400);
  };


  const handleBlur = (e) => {
    setTimeout(() => {
      //  Skip if option selection happened
      if (optionSelectedRef.current) return;

      setIsFocused(false);
      const term = searchTerm != null ? String(searchTerm).trim() : '';
      if (!term) {
        onChangeValue?.('');
        return;
      }

      const matchedOption = options.find(
        (opt) =>
          String(opt.label).toLowerCase() === term.toLowerCase() ||
          String(opt.value).toLowerCase() === term.toLowerCase()
      );

      if (matchedOption) {
      // For percent field, normalize value here as well
      const value = isPercent && !isNaN(matchedOption.value)
        ? Number(matchedOption.value) / 100
        : matchedOption.value;

      setSearchTerm(isPercent ? String(Number(matchedOption.value)) : String(matchedOption.value));
      onChangeValue?.(value);
      return;
    }

      let finalTerm = term;
      if (decimalPlaces > 0 && /^\d*\.?\d+$/.test(term)) {
        const rounded = roundHalfUpToDecimals(term, decimalPlaces);
        finalTerm = String(rounded);
        if (finalTerm !== term) setSearchTerm(finalTerm);
      }

      const n = Number(finalTerm);
      if (Number.isFinite(n)) {
        const parsed = isPercent ? n / 100 : n;
        onChangeValue?.(parsed);
      } else {
        onChangeValue?.(term);
      }
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (!isFocused || !options.length) return;

    if (e.key === 'ArrowDown') {
      // Move focus down
      setActiveIndex((prevIndex) => {
        const newIndex = (prevIndex + 1) % options.length;
        optionsRefs.current[newIndex]?.scrollIntoView({ block: 'nearest' });
        return newIndex;
      });
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      // Move focus up
      setActiveIndex((prevIndex) => {
        const newIndex = (prevIndex - 1 + options.length) % options.length;
        optionsRefs.current[newIndex]?.scrollIntoView({ block: 'nearest' });
        return newIndex;
      });
      e.preventDefault();
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      // Select the active option
      optionSelected(options[activeIndex]);
      e.preventDefault();
    } else if (e.key === 'Tab') {
      // Close the dropdown on Tab
      setIsFocused(false);
    }
    e.stopPropagation();
  };

  const dropdownToggle = (ev) => {
    ev.stopPropagation();
    setIsFocused(!isFocused);

    if (!isFocused && inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className={classNames(css.clsSearchBox)} ref={InputContainerRef}>
      <input
        type="text"
        placeholder={placeHolder || 'Select'}
        className={isFocused ? classNames(css.clsSearchInput, css.clsSearchInputActive) : css.clsSearchInput}
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        ref={inputRef}
        readOnly={searchDisabled}
        id={'newuifilterRef'+ uuidv4()}
        maxLength={maxLength}
      />
      <span className={classNames(css.clsAutoSearchArrow, css.clsAutoSearchRotate)}>
        <ReactSVG
          src={isFocused ? SearchUpArrow : SearchArrow}
          className={css.svgArrowIcon}
          onClick={dropdownToggle}
        />
      </span>

      {isFocused && (
        <div className={css.clsAutoSearchDropdown}>
          <ul className={css.clsAutoSearchList}>
            {options.map((option, index) => (
              <li
                ref={(el) => (optionsRefs.current[index] = el)}
                id={'newuifilterRef' + uuidv4()}
                key={index}
                className={classNames(css.clsAutoSearchOption, {
                  [css.clsActiveOption]: index === activeIndex, // Highlight the active option
                })}
                onMouseDown={(ev) => {
                  ev.preventDefault(); 
                  ev.stopPropagation();
                  optionSelected(option);
                }}
              >
                {option.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AutoSearchDropdown;
